import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth-context'
import { useWorkspace } from '@/lib/workspace-context'

export interface EventWithStats {
  id: string
  name: string
  slug: string
  tenantSlug: string
  startDate: string
  endDate: string
  primaryColor: string
  status: 'draft' | 'active' | 'ended' | 'archived'
  totalLeads: number
  confirmedLeads: number
}

export function useEvents() {
  const { user } = useAuth()
  const { activeWorkspace } = useWorkspace()

  return useQuery({
    queryKey: ['events', activeWorkspace?.id],
    queryFn: async (): Promise<EventWithStats[]> => {
      if (!user || !activeWorkspace) return []

      // Fetch events for the active workspace
      const { data: events, error } = await supabase
        .from('events')
        .select(`
          id,
          name,
          slug,
          start_date,
          end_date,
          primary_color,
          status,
          leads(id, doi_confirmed)
        `)
        .eq('tenant_id', activeWorkspace.id)
        .neq('status', 'archived')
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error fetching events:', error)
        throw error
      }

      return (events || []).map((event) => {
        const leads = event.leads as Array<{ id: string; doi_confirmed: boolean }> || []
        return {
          id: event.id,
          name: event.name,
          slug: event.slug,
          tenantSlug: activeWorkspace.slug,
          startDate: event.start_date,
          endDate: event.end_date,
          primaryColor: event.primary_color || '#3b82f6',
          status: event.status as EventWithStats['status'],
          totalLeads: leads.length,
          confirmedLeads: leads.filter((l) => l.doi_confirmed).length,
        }
      })
    },
    enabled: !!user && !!activeWorkspace,
  })
}

export function useEvent(eventId: string) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['event', eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('events')
        .select(`
          *,
          leads(id, doi_confirmed),
          tenants(slug)
        `)
        .eq('id', eventId)
        .single()

      if (error) throw error
      return data
    },
    enabled: !!user && !!eventId,
  })
}

interface CreateEventInput {
  name: string
  slug: string
  downloadUrl: string
  startDate: string
  endDate: string
  primaryColor?: string
}

export function useCreateEvent() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { activeWorkspace } = useWorkspace()

  return useMutation({
    mutationFn: async (input: CreateEventInput) => {
      if (!user) throw new Error('Not authenticated')
      if (!activeWorkspace) throw new Error('No workspace selected')

      const { data, error } = await supabase
        .from('events')
        .insert({
          tenant_id: activeWorkspace.id,
          created_by: user.id,
          name: input.name,
          slug: input.slug,
          download_url: input.downloadUrl,
          start_date: input.startDate,
          end_date: input.endDate,
          primary_color: input.primaryColor || '#3b82f6',
        })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] })
    },
  })
}

export function useArchiveEvent() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (eventId: string) => {
      const { error } = await supabase
        .from('events')
        .update({ status: 'archived' })
        .eq('id', eventId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] })
    },
  })
}
